'use client';

/**
 * Route-level error boundary — Profileia (2026-09-15 redesign).
 *
 * Catches errors in any route below src/app/. The root layout still
 * renders around this boundary, so Tailwind + shadcn/ui are available.
 *
 * SPEC-244: redesigned to match the global-error.tsx branded style —
 * Profileia gradient, animated pulse, error type detection, auto-retry.
 *
 * §6 Anti-Invención: pure UI — no business logic.
 */

import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, Home, AlertTriangle, Wrench, Clock } from 'lucide-react';

type ErrorType = 'server-down' | 'error' | 'timeout';

function detectErrorType(error: Error & { digest?: string }): ErrorType {
  const msg = (error.message || '').toLowerCase();
  if (msg.includes('fetch') || msg.includes('network') || msg.includes('econnrefused')) {
    return 'server-down';
  }
  if (msg.includes('timeout') || msg.includes('aborted')) {
    return 'timeout';
  }
  return 'error';
}

const ERROR_CONFIG: Record<ErrorType, { title: string; subtitle: string; icon: typeof AlertTriangle }> = {
  'server-down': {
    title: 'Estamos de mantenimiento',
    subtitle: 'Nuestros servidores están reiniciándose. Volveremos en breve.',
    icon: Wrench,
  },
  'timeout': {
    title: 'La conexión tardó demasiado',
    subtitle: 'La solicitud superó el tiempo límite. Intenta de nuevo.',
    icon: Clock,
  },
  'error': {
    title: 'Algo salió mal',
    subtitle: 'Se produjo un error inesperado al cargar esta página. Puedes intentar de nuevo o volver al inicio.',
    icon: AlertTriangle,
  },
};

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [countdown, setCountdown] = useState(10);
  const [checking, setChecking] = useState(false);
  const errorType = detectErrorType(error);
  const config = ERROR_CONFIG[errorType];
  const Icon = config.icon;

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') console.error('route error boundary caught:', error);
  }, [error]);

  // Auto-retry countdown for server-down
  useEffect(() => {
    if (errorType !== 'server-down' || countdown <= 0) return;
    const timer = setTimeout(() => {
      setCountdown((c) => c - 1);
      if (countdown === 1) {
        setChecking(true);
        fetch('/', { method: 'HEAD', cache: 'no-store' })
          .then(() => reset())
          .catch(() => {
            setChecking(false);
            setCountdown(10);
          });
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [errorType, countdown, reset]);

  const handleRetry = useCallback(() => {
    setCountdown(10);
    setChecking(false);
    reset();
  }, [reset]);

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 px-4 py-12"
    >
      <div className="max-w-md w-full text-center space-y-6">
        {/* Profileia logo gradient circle */}
        <div className="flex justify-center">
          <div className="size-16 rounded-full bg-gradient-to-br from-[#2563EB] to-[#06B6D4] flex items-center justify-center shadow-lg shadow-[#2563EB]/20 animate-pulse">
            <Icon className="size-8 text-white" aria-hidden="true" />
          </div>
        </div>

        {/* Title + subtitle */}
        <div className="space-y-2">
          <h1 className="text-2xl font-bold tracking-tight">
            {config.title}
          </h1>
          <p className="text-muted-foreground leading-relaxed text-sm">
            {config.subtitle}
          </p>
        </div>

        {/* Auto-retry countdown */}
        {errorType === 'server-down' && !checking && countdown > 0 && (
          <p className="text-xs text-muted-foreground">
            Reintentando en {countdown}s…
          </p>
        )}
        {checking && (
          <p className="text-xs text-muted-foreground">
            Verificando conexión…
          </p>
        )}

        {/* Buttons */}
        <div className="flex flex-wrap gap-3 justify-center">
          <Button onClick={handleRetry} className="gap-2 bg-gradient-to-r from-[#2563EB] to-[#06B6D4]">
            <RefreshCw className="size-4" aria-hidden="true" />
            Reintentar
          </Button>
          <Button asChild variant="outline" className="gap-2">
            <a href="/">
              <Home className="size-4" aria-hidden="true" />
              Volver al inicio
            </a>
          </Button>
        </div>

        {/* Error digest */}
        {error?.digest && (
          <p className="text-xs text-muted-foreground pt-2">
            Código: <code className="font-mono">{error.digest}</code>
          </p>
        )}

        {/* Footer */}
        <div className="pt-4 text-xs text-muted-foreground/60">
          <p className="font-semibold">Profileia</p>
          <p>La casa del emprendedor</p>
        </div>
      </div>
    </div>
  );
}
